import type { Pane } from "./layout/pane.ts";
import { findPost, loadPostBody } from "./blog.ts";

/* ───────────────────────────────────────────────────────────────
   URL routing for the content pane.

   Every pane state has an address, so a post the visitor is reading can
   be linked, bookmarked, and shared:

     /                     terminal only
     /blog/<slug>          terminal + pane showing that post

   Direct hits on /blog/<slug> are answered by the *static* page the build
   generates (see scripts/build-blog.ts) — a real crawlable document, not
   this app. This router handles the in-app case: opening a pane without a
   page load, and keeping the address bar honest while it's open.
   ─────────────────────────────────────────────────────────────── */

const POST_PATH = /^\/blog\/([A-Za-z0-9._-]+)\/?$/;

export class Router {
  private pane: Pane;

  constructor(pane: Pane) {
    this.pane = pane;
    addEventListener("popstate", () => void this.syncFromUrl(false));
  }

  /** Read the current URL and bring the pane in line with it. */
  async syncFromUrl(replace = true): Promise<void> {
    const match = POST_PATH.exec(location.pathname);
    if (!match) {
      if (this.pane.isOpen) this.pane.close();
      return;
    }
    const slug = match[1]!;
    if (this.pane.key === slug) return;
    await this.showPost(slug, { push: false, replace });
  }

  /** Open a post in the pane and put it in the address bar. */
  async openPost(slug: string): Promise<void> {
    await this.showPost(slug, { push: true, replace: false });
  }

  closePane(): void {
    if (!this.pane.isOpen) return;
    this.pane.close();
    history.pushState(null, "", "/");
  }

  private async showPost(
    slug: string,
    opts: { push: boolean; replace: boolean },
  ): Promise<void> {
    const url = `/blog/${slug}`;

    // Open immediately with a placeholder so the layout doesn't wait on the
    // network — the pane is the feedback that the command worked.
    this.pane.open(slug, { title: slug, html: "" });
    this.pane.setMessage(slug, "loading…");

    if (opts.push) history.pushState(null, "", url);
    else if (opts.replace) history.replaceState(null, "", url);

    try {
      const [meta, body] = await Promise.all([findPost(slug), loadPostBody(slug)]);
      // A later navigation may have won the race while we awaited.
      if (this.pane.key !== slug) return;
      this.pane.open(slug, { title: meta?.title ?? slug, html: body });
    } catch (err) {
      if (this.pane.key !== slug) return;
      this.pane.setMessage("not found", `Could not load "${slug}": ${String(err)}`);
    }
  }
}
